// src/realtime/RealtimeProvider.tsx
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";

import { API_BASE, api, call } from "@/api/client";
import { session } from "@/auth/session";
import { useToast } from "@/design-system";

import { RealtimeClient } from "./client";
import { applyEvent } from "./sync";
import { sseTransport, wsTransport } from "./transports";
import type { Channel, RealtimeStatus } from "./types";

const CHANNEL: Channel = import.meta.env.VITE_REALTIME_TRANSPORT === "ws" ? "ws" : "sse";
/** لا نُظهر "غير متصل" قبل مرور هذه المدة، تفادياً لوميض الشبكة العابر. */
const UI_GRACE_MS = 5000;

const Ctx = createContext<{ status: RealtimeStatus }>({ status: "idle" });
export const useRealtimeStatus = () => useContext(Ctx).status;

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const toast = useToast();
  // نراقب الرمز نفسه: بعد كل تجديد يُعاد فتح القناة الحية بتذكرة جديدة مرتبطة بالرمز الجديد
  const token = useSyncExternalStore(session.subscribe, session.token);
  const authed = !!token;
  const [status, setStatus] = useState<RealtimeStatus>("idle");
  const [shown, setShown] = useState<RealtimeStatus>("idle");

  useEffect(() => {
    if (!authed) return setStatus("idle");
    const transport = CHANNEL === "ws" ? wsTransport({ baseUrl: API_BASE }) : sseTransport({ baseUrl: API_BASE });
    const client = new RealtimeClient({
      transport,
      getTicket: async () =>
        (await call(api.POST("/api/stream/ticket", { params: { query: { channel: CHANNEL } } }))).ticket,
      onStatus: setStatus,
      onEvent: (ev) => {
        applyEvent(qc, ev);
        if (ev.type === "meter.alert_opened") toast.push("تنبيه استهلاك جديد على أحد العدادات", "bad");
      },
      onReady: (first) => {
        if (!first) void qc.invalidateQueries(); // لا replay: نجلب الحالة بعد كل إعادة اتصال
      },
      onFrame: (f) => {
        if (f.kind === "session_expiring") toast.push("ستنتهي جلستك خلال أقل من دقيقة", "warn");
        if (f.kind === "scope_changed") void qc.invalidateQueries({ queryKey: ["access"] });
      },
      onAuthLost: () => session.clear(),
    });
    client.start();
    return () => client.stop();
  }, [token, authed, qc, toast]);

  useEffect(() => {
    if (status === "live" || status === "idle") return setShown(status);
    const t = setTimeout(() => setShown(status), UI_GRACE_MS);
    return () => clearTimeout(t);
  }, [status]);

  return <Ctx.Provider value={{ status: shown }}>{children}</Ctx.Provider>;
}
